import type { SupabaseClient, User } from "@supabase/supabase-js";

export interface AuthCredentials {
  email: string;
  password: string;
  nickname?: string;
  school?: string;
  inviteCode?: string;
}

export interface PhoneAuthCredentials {
  phone: string;
  otp?: string;
}

export interface AuthResult {
  user: User | null;
  error: string | null;
  requiresOtp?: boolean;
  remainingAttempts?: number;
  lockedUntil?: Date | null;
}

interface SignOutResult {
  success: boolean;
  error?: string;
}

/**
 * 验证邮箱格式
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * 获取客户端 IP 地址（用于登录限制追踪）
 * 注意：实际 IP 应由服务端获取，这里提供备用方法
 */
function getClientInfo(): { ipAddress: string | null; userAgent: string | null } {
  return {
    ipAddress: null, // 服务端 RPC 会自动获取真实 IP
    userAgent: typeof window !== "undefined" ? window.navigator.userAgent : null,
  };
}

/**
 * 检查账户是否被锁定
 */
async function checkLoginLocked(
  supabase: SupabaseClient,
  email: string,
): Promise<{ isLocked: boolean; lockedUntil: Date | null }> {
  const { data, error } = await supabase.rpc("check_login_locked", {
    p_email: email,
  });

  if (error) {
    console.error("check_login_locked RPC error:", error);
    return { isLocked: false, lockedUntil: null };
  }

  if (data) {
    // 需要查询锁定时间
    const { data: statusData } = await supabase.rpc("get_login_attempt_status", {
      p_email: email,
    });

    if (statusData && statusData.length > 0) {
      const status = statusData[0];
      return {
        isLocked: true,
        lockedUntil: status.locked_until ? new Date(status.locked_until) : null,
      };
    }
  }

  return { isLocked: false, lockedUntil: null };
}

/**
 * 记录登录尝试
 */
async function recordLoginAttempt(
  supabase: SupabaseClient,
  params: {
    userId: string | null;
    email: string;
    success: boolean;
  },
): Promise<void> {
  const { ipAddress, userAgent } = getClientInfo();

  await supabase.rpc("record_login_attempt", {
    p_user_id: params.userId,
    p_email: params.email,
    p_ip_address: ipAddress,
    p_user_agent: userAgent,
    p_success: params.success,
  });
}

/**
 * 获取登录尝试状态
 */
async function getLoginAttemptStatus(
  supabase: SupabaseClient,
  email: string,
): Promise<{
  isLocked: boolean;
  lockedUntil: Date | null;
  attemptCount: number;
  secondsUntilUnlock: number;
}> {
  const { data, error } = await supabase.rpc("get_login_attempt_status", {
    p_email: email,
  });

  if (error || !data || data.length === 0) {
    return {
      isLocked: false,
      lockedUntil: null,
      attemptCount: 0,
      secondsUntilUnlock: 0,
    };
  }

  const status = data[0];
  return {
    isLocked: status.is_locked || false,
    lockedUntil: status.locked_until ? new Date(status.locked_until) : null,
    attemptCount: status.attempt_count || 0,
    secondsUntilUnlock: status.seconds_until_unlock || 0,
  };
}

/**
 * 格式化锁定时间显示
 */
function formatLockTime(seconds: number): string {
  if (seconds <= 0) return "";

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}分${remainingSeconds > 0 ? `${remainingSeconds}秒` : ""}`;
  }
  return `${remainingSeconds}秒`;
}

/**
 * 验证手机号格式（支持国际格式）
 */
function isValidPhone(phone: string): boolean {
  const phoneRegex = /^\+[1-9]\d{1,14}$/;
  return phoneRegex.test(phone);
}

/**
 * 邮箱注册（支持邀请码）
 */
export async function signUpWithEmail(
  supabase: SupabaseClient,
  credentials: AuthCredentials,
): Promise<AuthResult> {
  const { email, password, nickname, school, inviteCode } = credentials;

  // 验证输入
  if (!isValidEmail(email)) {
    return { user: null, error: "请输入有效的邮箱地址" };
  }

  if (password.length < 6) {
    return { user: null, error: "密码至少需要6个字符" };
  }

  // 邀请码验证延迟到邮箱验证回调（verify-email Edge Function）中完成
  // 注册时仅将邀请码存入 user_metadata，邮箱确认后再关联

  const displayName = nickname?.trim() || email.split("@")[0];

  // 调用 Supabase Auth 注册
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        nickname: displayName,
        school: school?.trim() || null,
        invite_code: inviteCode || null,
      },
    },
  });

  if (error) {
    return { user: null, error: error.message };
  }

  // 注册成功后，存储邮箱到 localStorage 供邮箱验证回调使用
  if (typeof window !== "undefined" && data.user) {
    localStorage.setItem("pending_verification_email", email);
    localStorage.setItem("pending_verification_user_id", data.user.id);
    if (inviteCode) {
      localStorage.setItem("pending_invite_code", inviteCode);
    }
  }

  // 如果有邀请码，在用户确认邮箱后关联邀请码
  // 注意：这里先不调用 use_invitation_code，因为用户还未确认邮箱
  // 邀请码关联将在邮箱验证回调 verify-email Edge Function 中完成

  return { user: data.user, error: null };
}

/**
 * 邮箱登录（带登录尝试次数限制）
 */
export async function signInWithEmail(
  supabase: SupabaseClient,
  credentials: AuthCredentials,
): Promise<AuthResult> {
  const { email, password } = credentials;

  if (!email.trim() || !password.trim()) {
    return { user: null, error: "请输入邮箱和密码" };
  }

  // 1. 检查账户是否被锁定
  const lockCheck = await checkLoginLocked(supabase, email);
  if (lockCheck.isLocked && lockCheck.lockedUntil) {
    const remainingSeconds = Math.max(
      0,
      Math.floor((lockCheck.lockedUntil.getTime() - Date.now()) / 1000),
    );
    const lockTimeDisplay = formatLockTime(remainingSeconds);
    return {
      user: null,
      error: `账户已被锁定，请在${lockTimeDisplay}后重试`,
      lockedUntil: lockCheck.lockedUntil,
    };
  }

  // 2. 执行登录
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    // 3. 登录失败，记录尝试并返回剩余次数
    const status = await getLoginAttemptStatus(supabase, email);
    const maxAttempts = 5;
    const remainingAttempts = maxAttempts - status.attemptCount;

    // 记录此次失败尝试
    await recordLoginAttempt(supabase, {
      userId: null,
      email,
      success: false,
    });

    // 重新检查锁定状态
    const newStatus = await getLoginAttemptStatus(supabase, email);

    if (newStatus.isLocked && newStatus.lockedUntil) {
      const remainingSeconds = Math.max(
        0,
        Math.floor((newStatus.lockedUntil.getTime() - Date.now()) / 1000),
      );
      const lockTimeDisplay = formatLockTime(remainingSeconds);
      return {
        user: null,
        error: `账户已被锁定，请在${lockTimeDisplay}后重试`,
        lockedUntil: newStatus.lockedUntil,
      };
    }

    // 返回失败原因和剩余尝试次数
    if (remainingAttempts <= 0) {
      return {
        user: null,
        error: "登录尝试次数过多，账户已被锁定，请在30分钟后重试",
        lockedUntil: newStatus.lockedUntil,
      };
    }

    return {
      user: null,
      error: error.message,
      remainingAttempts,
    };
  }

  // 4. 登录成功，记录成功尝试
  await recordLoginAttempt(supabase, {
    userId: data.user?.id || null,
    email,
    success: true,
  });

  return { user: data.user, error: null };
}

/**
 * 手机号登录/注册（OTP）
 */
export async function signInWithPhone(
  supabase: SupabaseClient,
  credentials: PhoneAuthCredentials,
): Promise<AuthResult> {
  const { phone, otp } = credentials;

  if (!isValidPhone(phone)) {
    return { user: null, error: "请输入有效的手机号（国际格式，如 +8613800138000）" };
  }

  // 如果提供了 OTP，验证它
  if (otp) {
    const { data, error } = await supabase.auth.verifyOtp({
      phone,
      token: otp,
      type: "sms",
    });

    if (error) {
      return { user: null, error: error.message };
    }

    // 用户资料由数据库触发器 handle_new_user() 自动创建

    return { user: data.user, error: null };
  }

  // 发送 OTP
  const { error } = await supabase.auth.signInWithOtp({
    phone,
  });

  if (error) {
    return { user: null, error: error.message };
  }

  return { user: null, error: null, requiresOtp: true };
}

/**
 * 登出
 */
export async function signOut(supabase: SupabaseClient): Promise<SignOutResult> {
  const { error } = await supabase.auth.signOut();

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * 获取当前用户
 */
export async function getCurrentUser(
  supabase: SupabaseClient,
): Promise<AuthResult> {
  const { data, error } = await supabase.auth.getUser();

  if (error) {
    return { user: null, error: error.message };
  }

  return { user: data.user, error: null };
}

/**
 * 获取当前会话
 */
export async function getCurrentSession(supabase: SupabaseClient) {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    return { session: null, error: error.message };
  }

  return { session: data.session, error: null };
}

/**
 * 监听认证状态变化
 */
export function onAuthStateChange(
  supabase: SupabaseClient,
  callback: (event: string, user: User | null) => void,
): { unsubscribe: () => void } {
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session?.user ?? null);
  });

  return {
    unsubscribe: () => data.subscription.unsubscribe(),
  };
}

/**
 * 重置密码（发送重置邮件）
 */
export async function resetPassword(
  supabase: SupabaseClient,
  email: string,
): Promise<{ success: boolean; error?: string }> {
  if (!isValidEmail(email)) {
    return { success: false, error: "请输入有效的邮箱地址" };
  }

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * 更新密码
 */
export async function updatePassword(
  supabase: SupabaseClient,
  newPassword: string,
): Promise<{ success: boolean; error?: string }> {
  if (newPassword.length < 6) {
    return { success: false, error: "密码至少需要6个字符" };
  }

  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * 更新用户邮箱
 */
export async function updateEmail(
  supabase: SupabaseClient,
  newEmail: string,
): Promise<{ success: boolean; error?: string }> {
  if (!isValidEmail(newEmail)) {
    return { success: false, error: "请输入有效的邮箱地址" };
  }

  const { error } = await supabase.auth.updateUser({
    email: newEmail,
  });

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * 第三方 OAuth 登录
 */
export async function signInWithOAuth(
  supabase: SupabaseClient,
  provider: "google" | "github" | "twitter",
): Promise<{ url: string | null; error?: string }> {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
    },
  });

  if (error) {
    return { url: null, error: error.message };
  }

  return { url: data.url };
}
